import React, { useEffect, useRef, useState } from "react";
import { initHighchartsModules } from "../../../../../utils/initHighchartsModules";
import {
  Button,
  Card,
  Col,
  Dropdown,
  DropdownButton,
  Row,
} from "react-bootstrap";
import type { Options } from "highcharts";
import AccordionComponent from "../../../../../common/Accordion";
import MonthIntervalDropdown from "../../../../../common/MonthIntervalDropdown";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import HighchartExportButton from "../../../../../common/HighchartExportButton";
import TopLimitDropdown from "../../../../../common/TopLimitDropdown";
import { getHREOverviewSummary } from "../../../../../services/ActionStatusApi";

interface HREStackBarSummaryProps {
  openDropdown: string | null;
  setOpenDropdown: React.Dispatch<React.SetStateAction<string | null>>;
}

const STATUS_COLORS: Record<string, string> = {
  Overdue: "#fe6767",
  Draft: "#E6C872",
  Scheduled: "#60a5fa",
  Pending: "#cf8282",
  Completed: "#02af5e",
  NotScheduled: "#33919f",

  // ROR
  InReview: "#60a5fa",
  DraftReview: "#E6C872",
  ReviewCompleted: "#02af5e",
  NotInitiated: "#33919f",

  // Editor
  EditingPending: "#33919f",
  EditingInProgress: "#60a5fa",
  ApprovedByDoctor: "#0bf364",
  PendingDoctorApproval: "#CF646D",
  DwcExtension: "#4176ac",
  SentForRework: "#aa12be",
  Exception: "#F8A565",
};

const STATUS_ORDER: Record<string, string[]> = {
  Historian: [
    "Overdue",
    "Draft",
    "Pending",
    "Completed",
    "NotScheduled",
    "Exception",
  ],
  ROR: [
    "Overdue",
    "DraftReview",
    "InReview",
    "ReviewCompleted",
    "NotInitiated",
    "Exception",
  ],
  Editor: [
    "Overdue",
    "EditingPending",
    "EditingInProgress",
    "PendingDoctorApproval",
    "ApprovedByDoctor",
    "SentForRework",
    "Completed",
    "DwcExtension",
    "Exception",
  ],
};

const HREStackBarSummary: React.FC<HREStackBarSummaryProps> = ({
  openDropdown,
  setOpenDropdown,
}) => {
  const [monthInterval, setMonthInterval] = useState<string>("1");
  const [intervalLabel, setIntervalLabel] = useState<string>("This Month");
  const [summaryData, setSummaryData] = useState<any[]>([]);
  const [activeRole, setActiveRole] = useState("Historian");
  const [chartOptions, setChartOptions] = useState<Highcharts.Options>({});

  const [hiddenStatuses, setHiddenStatuses] = useState<
    Record<string, Set<string>>
  >({});

  useEffect(() => {
    initHighchartsModules();
  }, []);

  const fetchHREOverviewData = async () => {
    try {
      const result = await getHREOverviewSummary(monthInterval);
      setSummaryData(result);
    } catch (error) {
      console.error("Failed to load case overview data", error);
    }
  };

  useEffect(() => {
    fetchHREOverviewData();
  }, [monthInterval]);

  const normalizedData = summaryData.map((item) => ({
    processName: item.processName,
    statusCounts: JSON.parse(item.statusCounts),
  }));

  const formatStatusLabel = (status: string) => {
    return status.replace(/([A-Z])/g, " $1").trim();
  };

  const buildChartOptions = (process: any): Highcharts.Options => {
    const statusCounts = process.statusCounts as Record<string, number>;
    const hidden = hiddenStatuses[process.processName] || new Set();

    const order =
      STATUS_ORDER[process.processName] || Object.keys(statusCounts);

    const series: Highcharts.SeriesOptionsType[] = [...order].reverse().map(
      (status): Highcharts.SeriesBarOptions => ({
        type: "bar",
        name: formatStatusLabel(status),
        data: [statusCounts[status] ?? 0],
        color: STATUS_COLORS[status] || "#999",
        visible: !hidden.has(status),
      }),
    );

    return {
      chart: {
        type: "bar",
        height: 50,
      },
      exporting: { enabled: false },
      title: { text: undefined },

      xAxis: {
        categories: [process.processName],
        visible: false,
      },

      yAxis: {
        min: 0,
        title: { text: undefined },
        gridLineWidth: 0,
        labels: { enabled: false },
      },

      legend: { enabled: false },

      tooltip: {
        formatter: function () {
          return `<b>${this.series.name}</b>: ${this.y}`;
        },
      },

      plotOptions: {
        bar: {
          stacking: "percent",
          pointWidth: 60,
          dataLabels: {
            enabled: true,
            formatter: function (this: Highcharts.Point) {
              return this.y && this.y > 0 ? this.y : "";
            },
            style: {
              color: "#fff",
              fontWeight: "bold",
              textOutline: "none",
              fontSize: "14px",
            },
          },
        },
      },

      credits: { enabled: false },
      series,
    };
  };

  const toggleStatus = (processName: string, status: string) => {
    setHiddenStatuses((prev) => {
      const processHidden = new Set(prev[processName] || []);

      if (processHidden.has(status)) {
        processHidden.delete(status);
      } else {
        processHidden.add(status);
      }

      return {
        ...prev,
        [processName]: processHidden,
      };
    });
  };

  const handleIntervalChange = (value: string | number, label: string) => {
    setMonthInterval(String(value));
    setIntervalLabel(label);
  };

  const [caseSummaryKey, setCaseSummaryKey] = useState<string | null>("0");

  const handleBodyAccordionSelect = (key: string | null) => {
    // Prevent collapsing if the same key is clicked
    if (caseSummaryKey === key) {
      return; // do nothing
    }
    setCaseSummaryKey(key || "0");
  };

  const summaryAccordion = (
    items: { title: JSX.Element; content: JSX.Element }[],
  ) => (
    <AccordionComponent
      items={items.map((item, index) => ({
        ...item,
      }))}
      activeKey={caseSummaryKey}
      onSelect={handleBodyAccordionSelect}
    />
  );

  const chartComponentRef = useRef<HighchartsReact.RefObject>(null);

  useEffect(() => {
    const closeAll = () => setOpenDropdown(null);
    window.addEventListener("closeAllDropdowns", closeAll);
    return () => window.removeEventListener("closeAllDropdowns", closeAll);
  }, []);

  const hasAnyData = (statusCounts: Record<string, number>) => {
    return Object.values(statusCounts).some((value) => value > 0);
  };

  return (
    <div className="bg-white rounded-lg shadow-md">
      {summaryAccordion([
        {
          title: (
            <div
              className="d-flex  align-items-center w-100 "
              onClick={(e) => e.stopPropagation()}
            >
              <span className="me-auto">Overview</span>

              <div className="d-flex gap-2">
                <MonthIntervalDropdown
                  title={intervalLabel}
                  onSelect={handleIntervalChange}
                  show={openDropdown === "hreStackInterval"}
                  onToggle={(isOpen) =>
                    setOpenDropdown(isOpen ? "hreStackInterval" : null)
                  }
                />
              </div>
            </div>
          ),
          content: (
            <>
              {normalizedData.map((process, index) => (
                <div key={index} className="mb-2 w-100">
                  <div>{process.processName}</div>

                  {hasAnyData(process.statusCounts) ? (
                    <>
                      <HighchartsReact
                        highcharts={Highcharts}
                        options={buildChartOptions(process)}
                      />

                      {/* Legend */}
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "12px",
                          marginTop: "6px",
                        }}
                      >
                        {(
                          STATUS_ORDER[process.processName] ||
                          Object.keys(process.statusCounts)
                        ).map((status) => {
                          const isHidden =
                            hiddenStatuses[process.processName]?.has(status);

                          return (
                            <div
                              key={status}
                              onClick={() =>
                                toggleStatus(process.processName, status)
                              }
                              style={{
                                display: "flex",
                                alignItems: "center",
                                fontSize: "11px",
                                cursor: "pointer",
                                opacity: isHidden ? 0.4 : 1,
                              }}
                            >
                              <span
                                style={{
                                  width: "14px",
                                  height: "14px",
                                  backgroundColor:
                                    STATUS_COLORS[status] || "#999",
                                  marginRight: "6px",
                                  borderRadius: "3px",
                                }}
                              />
                              {formatStatusLabel(status)}
                            </div>
                          );
                        })}
                      </div>
                    </>
                  ) : (
                    <div
                      style={{
                        height: "50px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <span
                        style={{
                          color: "#ff2929",

                          fontSize: "13px",
                        }}
                      >
                        No record found
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </>
          ),
        },
      ])}
    </div>
  );
};

export default HREStackBarSummary;
